<script lang="ts" setup>
import { number, object } from "yup";
import { Button } from "@/components/Button";
import type {
  PatrimonialData,
  PopulatePatrimonialDataInput,
} from "~/graphql/generated/graphql";
import { useUpdateUserPatrimonialData } from "~/composables/api/account/patrimonial/useUpdateUserPatrimonialData";
import { useCheckPatrimonialData } from "~/composables/useCheckPatrimonialData";

const show = defineModel<boolean>({ default: false });

const { $tt } = useNuxtApp();
const notify = useNotify();
useCheckPatrimonialData({
  onShowPatrimonialDataFormModal: () => {
    show.value = true;
  },
});

const updateUserPatrimonialData = useUpdateUserPatrimonialData({
  onSuccess: () => {
    show.value = false;

    notify($tt("private.patrimonial.success"), {
      type: "success",
    });
  },
});

const loading = computed(() => updateUserPatrimonialData.isPending.value);

const requiredMessage = $tt("shared.requiredField");

const { handleSubmit } = useForm<PatrimonialData>({
  validationSchema: toTypedSchema(
    object({
      financialInvestments: number().required(requiredMessage),
      others: number().required(requiredMessage),
      monthlyIncome: number().required(requiredMessage),
      chattel: number().required(requiredMessage),
      personalty: number().required(requiredMessage),
      realOrAlternative: number().required(requiredMessage),
    }),
  ),
});

const onSubmit = handleSubmit((formValues) => {
  updateUserPatrimonialData.mutateAsync({
    input: formValues as PopulatePatrimonialDataInput,
  });
});
</script>

<template>
  <Transition>
    <div
      v-if="show"
      class="fixed inset-0 z-[999999] w-full max-w-[100vw] h-full flex items-center justify-center bg-black bg-opacity-60 p-2 md:p-8 font-nunito"
    >
      <div
        class="bg-[#1f282e] flex flex-col gap-8 justify-center rounded-lg p-4 shadow-lg w-full max-w-[800px] max-h-[96vh] md:p-8"
      >
        <h2 class="text-center text-gray-50 text-2xl">
          {{ $tt("private.patrimonial.patrimonialDataFormTitle") }}
        </h2>
        <div>
          <div class="p-3 rounded-xl flex gap-2 bg-[#171c20] text-white">
            <div
              class="bg-charcoal-gray text-white rounded-full w-5 h-5 flex flex-shrink-0 items-center justify-center"
            >
              !
            </div>
            <p class="text-base">
              {{ $tt("private.patrimonial.patrimonialDataFormDisclaimer") }}
            </p>
          </div>
        </div>
        <form
          class="flex flex-col h-full overflow-auto px-2 md:max-h-[500px]"
          autocomplete="off"
          @submit.prevent="onSubmit"
        >
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <PrivateFormInput
              name="monthlyIncome"
              :label="$tt('private.patrimonial.monthlyIncome.label')"
              input-type="money"
            />

            <PrivateFormInput
              name="chattel"
              :label="$tt('private.patrimonial.chattel.label')"
              input-type="money"
            />

            <PrivateFormInput
              name="financialInvestments"
              :label="$tt('private.patrimonial.financialInvestments.label')"
              input-type="money"
            />

            <PrivateFormInput
              name="personalty"
              :label="$tt('private.patrimonial.personalty.label')"
              input-type="money"
            />

            <PrivateFormInput
              name="realOrAlternative"
              :label="$tt('private.patrimonial.realOrAlternative.label')"
              input-type="money"
            />

            <PrivateFormInput
              name="others"
              :label="$tt('private.patrimonial.others.label')"
              input-type="money"
            />
          </div>

          <div>
            <div
              class="relative h-[25px] flex flex-col justify-center mb-4 mt-2"
            >
              <p
                v-if="updateUserPatrimonialData.isError.value"
                class="absolute text-center text-indian-red font-normal self-center"
              >
                {{ $tt("private.patrimonial.error") }}
              </p>
            </div>

            <div class="flex justify-center">
              <Button
                :loading="loading"
                :disabled="loading"
                type="submit"
                class="w-96 h-12 rounded-lg py-2 px-5 font-nunito"
              >
                {{ $tt("private.patrimonial.submit") }}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </Transition>
</template>
